<script setup lang="ts">
  import { onMounted } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { VueDraggable } from 'vue-draggable-plus';
  import { Plus, SquareMinus, Check } from 'lucide-vue-next';
  import { useElementProperties } from '../../../model/useElementProperties';
  import { useCheckboxAlternativesModal } from '../../../model/useCheckboxAlternativesModal';
  import { ALTERNATIVES_DRAGGABLE_OPTIONS } from '../../../lib/alternatives-draggable-options';
  import AlternativeDragHandle from './AlternativeDragHandle.vue';
  import { Input, UiModalHeader, ColoredButton, ThemeProvider } from '@/shared/ui';

  interface Props {
    elementId: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const { selectedElement, updateElement } = useElementProperties(() => props.elementId);

  const {
    localAlternatives,
    totalCount,
    initFromElement,
    addAlternative,
    removeAlternative,
    updateAlternative,
    save,
  } = useCheckboxAlternativesModal({ selectedElement, updateElement });

  function handleSave() {
    save();
    emit('close');
  }

  function handleClose() {
    emit('close');
  }

  onMounted(() => {
    initFromElement();
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[508px] max-h-[90vh] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="flex flex-col max-h-[90vh]">
      <!-- Header -->
      <UiModalHeader
        class="px-6 py-3 border-b border-gray-02 shrink-0"
        title="Alternativas Checkbox"
        title-size="text-sm"
        @close="handleClose"
      />

      <!-- Alternatives section -->
      <div class="flex-1">
        <p class="px-6 pt-6 pb-3 text-sm font-semibold text-gray-07">
          Alternativas
        </p>

        <div class="mx-6 mb-4">
          <div class="bg-white-01 border border-gray-02 rounded-2xl p-6">
            <VueDraggable
              v-model="localAlternatives"
              v-bind="ALTERNATIVES_DRAGGABLE_OPTIONS"
              class="space-y-3 h-64 overflow-y-auto pr-2 mb-3"
            >
              <div
                v-for="(alternative, index) in localAlternatives"
                :key="alternative.id"
                class="flex items-center gap-4"
              >
                <AlternativeDragHandle />
                <Input
                  :model-value="alternative.text"
                  :placeholder="`Opção ${index + 1}`"
                  class="flex-1 h-11"
                  @update:model-value="(val) => updateAlternative(index, String(val))"
                />
                <button
                  type="button"
                  class="shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                  :disabled="localAlternatives.length <= 1"
                  @click="removeAlternative(index)"
                >
                  <SquareMinus
                    :size="24"
                    class="text-[var(--theme-secondary-ink)]"
                  />
                </button>
              </div>
            </VueDraggable>
            <button
              type="button"
              class="w-full h-[42px] bg-[var(--theme-secondary)] rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
              @click="addAlternative"
            >
              <Plus
                :size="20"
                color="white"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Total -->
      <div class="px-10 pt-3 pb-6 border-b border-gray-02 shrink-0">
        <p class="text-sm text-gray-07">
          Total de Alternativas: {{ totalCount }}
        </p>
      </div>

      <!-- Footer -->
      <div class="px-6 py-8 flex justify-end gap-6 shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :icon="Check"
          :disabled="totalCount === 0"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
